// Mood Lanes — "Shape My Mood" guided funnel data.
// Each lane maps to existing personalize state: tags (sub-moods), tone, audience.
// Keep lists tight: 5-7 items per step. No giant chip walls.
//
// Each lane also exposes:
//   • emblem: the symbol that appears on the wax seal of the public-card sleeve
//   • emblemColor: tonal accent for the wax seal (kept restrained / desaturated)
//   • temperatures: lane-curated emotional intensity options for Step 3
//   • occasions: lane-curated context/occasion options for Step 4 (Phase 2)
//   • relationships: lane-curated audience targeting options for Step 5 (Phase 2)

export const LANES = [
    {
        key: "heart",
        label: "Heart & Relationships",
        glyph: "♡",
        emblem: "♡", emblemColor: "#A52A38",  // deep rose-wine
        subMoods: ["missing-someone", "in-love", "heartbreak", "rebuilding", "complicated", "letting-go"],
        temperatures: ["soft", "aching", "vulnerable", "hopeful", "longing", "tender"],
        occasions: ["just because", "anniversary", "missing them", "soft launch", "after a fight", "saying goodbye", "Valentine's"],
        relationships: ["partner", "ex", "crush", "situationship", "self", "no one in particular"],
        voices: [
            { key: "soft",      label: "Soft",       tone: "clean",  audience: "partner" },
            { key: "poetic",    label: "Poetic",     tone: "clean",  audience: "partner" },
            { key: "indirect",  label: "Indirect",   tone: "native", audience: "friends" },
            { key: "honest",    label: "Honest",     tone: "clean",  audience: "self" },
            { key: "savagelite",label: "Savage-lite",tone: "native", audience: "public" },
        ],
        prompt: "Say what you couldn't fully say.",
    },
    {
        key: "healing",
        label: "Healing & Self-Worth",
        glyph: "✺",
        emblem: "☼", emblemColor: "#B8893A",  // warm sun-gold
        subMoods: ["growth", "proud", "tired", "rebuilding", "becoming", "soft-day", "still-here"],
        temperatures: ["peaceful", "reflective", "tender", "grounded", "quiet", "hopeful"],
        occasions: ["just because", "comeback era", "after therapy", "boundary kept", "leaving the chapter", "sunday reset"],
        relationships: ["self", "past me", "friends", "the therapist's couch", "no one in particular"],
        voices: [
            { key: "tender",       label: "Tender",       tone: "clean",  audience: "self" },
            { key: "affirming",    label: "Affirming",    tone: "clean",  audience: "public" },
            { key: "honest",       label: "Honest",       tone: "native", audience: "self" },
            { key: "quiet",        label: "Quietly proud",tone: "native", audience: "self" },
        ],
        prompt: "What did this week ask of you?",
    },
    {
        key: "faith",
        label: "Faith & Gratitude",
        glyph: "✦",
        emblem: "✦", emblemColor: "#9A7B3D",  // candle-cream gold
        subMoods: ["grateful", "answered-prayer", "trust", "surrender", "blessed", "small-mercies"],
        temperatures: ["peaceful", "surrender", "grateful", "reverent", "hopeful", "comforting"],
        occasions: ["just because", "answered prayer", "before a hard day", "small mercy", "season of waiting", "thank-you note"],
        relationships: ["God", "self", "family", "community", "no one in particular"],
        voices: [
            { key: "prayerful",    label: "Prayerful",        tone: "clean",  audience: "self" },
            { key: "grateful",     label: "Grateful",         tone: "clean",  audience: "public" },
            { key: "encouraging",  label: "Encouraging",      tone: "clean",  audience: "friends" },
            { key: "scripture",    label: "Scripture-inspired",tone: "clean", audience: "public" },
        ],
        prompt: "What are you quietly thankful for?",
    },
    {
        key: "drive",
        label: "Drive & Discipline",
        glyph: "▲",
        emblem: "▲", emblemColor: "#2B2B2B",  // matte charcoal
        subMoods: ["locked-in", "early-mornings", "no-shortcuts", "building", "back-on-it", "focus"],
        temperatures: ["bold", "focused", "relentless", "patient", "confident", "measured"],
        occasions: ["just because", "launch day", "exam season", "promotion", "side hustle", "comeback", "year-end push"],
        relationships: ["self", "future me", "the team", "the doubters", "public"],
        voices: [
            { key: "bold",         label: "Bold",         tone: "clean", audience: "public" },
            { key: "minimal",      label: "Minimal",      tone: "clean", audience: "self" },
            { key: "cinematic",    label: "Cinematic",    tone: "clean", audience: "public" },
            { key: "motivational", label: "Motivational", tone: "clean", audience: "public" },
        ],
        prompt: "What are you grinding toward — say it loud or low.",
    },
    {
        key: "funny",
        label: "Funny / Petty / Unbothered",
        glyph: "◐",
        emblem: "◐", emblemColor: "#C97A4A",  // playful warm peach
        subMoods: ["unbothered", "petty", "moving-on", "no-thoughts", "iconic", "delulu"],
        temperatures: ["unhinged", "deadpan", "playful", "petty", "chaotic", "dramatic"],
        occasions: ["just because", "monday energy", "after a fight", "group-chat lore", "vibe check", "indirect", "main character moment"],
        relationships: ["the group chat", "an ex", "a hater", "no one (subtweet)", "the timeline", "myself, frankly"],
        voices: [
            { key: "funny",     label: "Funny",     tone: "native", audience: "friends" },
            { key: "savage",    label: "Savage",    tone: "native", audience: "public" },
            { key: "sarcastic", label: "Sarcastic", tone: "native", audience: "friends" },
            { key: "meme",      label: "Meme-like", tone: "native", audience: "public" },
        ],
        prompt: "What's the line you'd post but never say to their face?",
    },
    {
        key: "life",
        label: "Life Moments",
        glyph: "✧",
        emblem: "✧", emblemColor: "#A0884F",  // pale gold
        subMoods: ["birthday", "milestone", "ending", "beginning", "thank-you", "goodbye"],
        temperatures: ["celebratory", "warm", "reflective", "grateful", "tender", "wistful"],
        occasions: ["just because", "birthday", "graduation", "wedding", "new job", "moving day", "goodbye", "anniversary"],
        relationships: ["partner", "family", "best friend", "self", "everyone gathered"],
        voices: [
            { key: "celebratory", label: "Celebratory", tone: "clean", audience: "public" },
            { key: "reflective",  label: "Reflective",  tone: "clean", audience: "self" },
            { key: "warm",        label: "Warm",        tone: "clean", audience: "friends" },
            { key: "honest",      label: "Honest",      tone: "native", audience: "self" },
        ],
        prompt: "Mark the moment — in one breath.",
    },
    {
        key: "civic",
        label: "Politics & Social Issues",
        glyph: "✪",
        emblem: "△", emblemColor: "#7C1B26",  // sharp wine-red (torch)
        subMoods: ["fed-up", "speaking-up", "marching", "vote", "injustice", "hopeful-anyway", "tuned-in"],
        temperatures: ["fierce", "measured", "principled", "weary", "urgent", "hopeful"],
        occasions: ["just because", "election season", "protest", "anniversary of injustice", "policy moment", "community win", "in memoriam"],
        relationships: ["the public", "the powerful", "my community", "future generations", "the quiet majority"],
        voices: [
            { key: "measured",   label: "Measured",     tone: "clean",  audience: "public" },
            { key: "fierce",     label: "Fierce",       tone: "native", audience: "public" },
            { key: "rallying",   label: "Rallying",     tone: "clean",  audience: "public" },
            { key: "personal",   label: "Personal",     tone: "native", audience: "self" },
            { key: "hopeful",    label: "Hopeful",      tone: "clean",  audience: "public" },
        ],
        prompt: "What are you not staying quiet about anymore?",
    },
    {
        key: "banter",
        label: "Social Banter",
        glyph: "✦",
        emblem: "★", emblemColor: "#B8704C",  // peachy-bronze
        subMoods: ["group-chat", "dating-apps", "wfh", "monday-energy", "petty-win", "the-audacity", "side-eye"],
        temperatures: ["chaotic", "deadpan", "main-character", "unhinged", "dramatic", "observational"],
        occasions: ["just because", "monday energy", "group-chat lore", "dating app drop", "wfh chaos", "vibe check", "petty win"],
        relationships: ["the group chat", "the timeline", "an ex", "a coworker", "the universe", "no one specifically"],
        voices: [
            { key: "chaotic",       label: "Chaotic",       tone: "native", audience: "friends" },
            { key: "deadpan",       label: "Deadpan",       tone: "native", audience: "public" },
            { key: "main-character", label: "Main Character", tone: "native", audience: "public" },
            { key: "observational", label: "Observational", tone: "native", audience: "public" },
            { key: "savage",        label: "Savage",        tone: "native", audience: "friends" },
        ],
        prompt: "What's the most you've thought about today?",
    },
];

export const getLane = (key) => LANES.find((l) => l.key === key);


// ============================================================================
// Emotional compatibility — temperature × voice
// ============================================================================
// Map of: laneKey -> temperature -> [voice keys that feel emotionally
// dissonant when paired]. The conflicting voice isn't *disabled* — it's
// rendered with reduced opacity and a tiny "doesn't quite match" hint. The
// user can still pick it (sometimes dissonance is the point), but we nudge
// them toward voices that won't fight the temperature they just chose.
//
// Kept tight and conservative — only the most obviously contradictory pairs.

export const VOICE_CONFLICTS = {
    heart: {
        soft:       ["savagelite"],
        aching:     ["savagelite"],
        vulnerable: ["savagelite", "indirect"],
        tender:     ["savagelite"],
        longing:    ["savagelite"],
    },
    healing: {
        peaceful:   [],
        grounded:   [],
        quiet:      ["affirming"],
        tender:     [],
    },
    faith: {
        reverent:   [],
        surrender:  [],
        peaceful:   [],
    },
    drive: {
        patient:    ["bold"],
        measured:   ["motivational", "bold"],
        relentless: [],
    },
    funny: {
        deadpan:    [],
        unhinged:   [],
        petty:      [],
        playful:    ["savage"],
        chaotic:    [],
    },
    life: {
        wistful:    ["celebratory"],
        reflective: ["celebratory"],
        tender:     [],
        celebratory:["reflective"],
    },
    civic: {
        measured:   ["fierce"],
        weary:      ["fierce", "rallying"],
        urgent:     [],
        principled: [],
        fierce:     ["measured", "hopeful"],
        hopeful:    ["fierce"],
    },
    banter: {
        deadpan:    [],
        chaotic:    [],
        observational: ["unhinged"],
        dramatic:   [],
        unhinged:   [],
        "main-character": [],
    },
};

export const isVoiceConflicted = (laneKey, temperature, voiceKey) => {
    if (!laneKey || !temperature || !voiceKey) return false;
    const m = VOICE_CONFLICTS[laneKey];
    if (!m) return false;
    const list = m[temperature];
    if (!Array.isArray(list)) return false;
    return list.includes(voiceKey);
};


// ============================================================================
// Relationship → audience mapping (Phase 3·d)
// ============================================================================
// Replaces the brittle ladder previously inlined in MoodLanes.jsx apply().
// Each lane gets its own explicit map so we never rely on string-shape guessing.
// `audience` is one of: partner | self | friends | public — the same set the
// LLM prompt already understands (see generation.py `audience_hint`).

const REL_MAPS = {
    heart: {
        "partner": "partner", "ex": "partner", "crush": "partner",
        "situationship": "partner", "self": "self",
        "no one in particular": "public",
    },
    healing: {
        "self": "self", "past me": "self",
        "friends": "friends", "the therapist's couch": "self",
        "no one in particular": "public",
    },
    faith: {
        "god": "self", "self": "self",
        "family": "friends", "community": "public",
        "no one in particular": "public",
    },
    drive: {
        "self": "self", "future me": "self",
        "the team": "friends", "the doubters": "public", "public": "public",
    },
    funny: {
        "the group chat": "friends", "an ex": "partner", "a hater": "public",
        "no one (subtweet)": "public", "the timeline": "public",
        "myself, frankly": "self",
    },
    life: {
        "partner": "partner", "family": "friends", "best friend": "friends",
        "self": "self", "everyone gathered": "public",
    },
    civic: {
        "the public": "public", "the powerful": "public",
        "my community": "friends", "future generations": "public",
        "the quiet majority": "public",
    },
    banter: {
        "the group chat": "friends", "the timeline": "public",
        "an ex": "partner", "a coworker": "friends",
        "the universe": "public", "no one specifically": "public",
    },
};

export const relationshipToAudience = (laneKey, relationship, fallback = null) => {
    if (!relationship) return fallback;
    const m = REL_MAPS[laneKey];
    if (m && m[relationship]) return m[relationship];
    // Last-resort heuristic for unknown lanes/relationships.
    const lower = String(relationship).toLowerCase();
    if (lower === "self" || lower.includes("me")) return "self";
    if (lower.includes("partner") || lower.includes("ex") || lower.includes("crush") || lower.includes("situationship")) return "partner";
    if (lower.includes("friend") || lower.includes("chat") || lower.includes("family") || lower.includes("community") || lower.includes("team")) return "friends";
    return "public";
};





// ============================================================================
// Phase 3·c — Per-lane ambient palettes for the creation-flow background
// ============================================================================
// Very restrained tints — meant to be layered at ~6-12% opacity on top of the
// dark-navy modal/card backgrounds. Two-stop radial gradient so we get a soft
// warmth in the upper-third without flooding the whole canvas.
// Each entry returns the SAME hue family as the lane's wax-seal emblemColor.

const LANE_AMBIENT = {
    heart:   { from: "#7A1E2D", to: "#1A0509",   accent: "#A52A38" },  // deep rose-wine
    healing: { from: "#7A5A21", to: "#1A1306",   accent: "#B8893A" },  // warm sun-gold
    faith:   { from: "#6C5325", to: "#170F03",   accent: "#9A7B3D" },  // candle-cream gold
    drive:   { from: "#1F1F22", to: "#050507",   accent: "#3D3D44" },  // cold matte steel
    funny:   { from: "#8A4520", to: "#1B0A04",   accent: "#C97A4A" },  // playful warm peach
    life:    { from: "#6F5825", to: "#160F04",   accent: "#A0884F" },  // pale gold
    civic:   { from: "#5C1018", to: "#120203",   accent: "#7C1B26" },  // stormy wine-red
    banter:  { from: "#7C4427", to: "#180806",   accent: "#B8704C" },  // peachy-bronze
};

export const getLaneAmbient = (laneKey) => LANE_AMBIENT[laneKey] || null;


// ============================================================================
// Phase 3·b — Per-cluster card visual adaptation
// ============================================================================
// Tiny style nudges layered on top of existing pack treatments. NEVER override
// the pack — these are additive: a soft inset tint, a 1-2deg tilt, a slightly
// tighter line-height, etc. Lookup is (lane, temperature) → cluster style.
//
// Returns an object with strict shape:
//   tintRGBA:     rgba string for a 6-10% inset overlay on the card
//   tilt:         small rotation in degrees (default 0)
//   shadowBoost:  extra drop-shadow opacity 0-1 (default 0)
//   letterSpacing:CSS letter-spacing override (or null)
//   lineHeight:   CSS line-height override (or null)
//
// We only define entries that *meaningfully* differ from the pack default.
// Unknown clusters fall through to null (no adjustment) — pack treatment wins.

const CLUSTER_STYLES = {
    // ─── Heart ─────────────────────────────────────────────────
    "heart:aching":      { tintRGBA: "rgba(165, 42, 56, 0.10)",  tilt: 0,    shadowBoost: 0.10, lineHeight: 1.18 },
    "heart:vulnerable":  { tintRGBA: "rgba(165, 42, 56, 0.08)",  tilt: 0,    shadowBoost: 0.05, lineHeight: 1.20 },
    "heart:longing":     { tintRGBA: "rgba(165, 42, 56, 0.08)",  tilt: 0,    shadowBoost: 0.05, lineHeight: 1.18 },
    "heart:tender":      { tintRGBA: "rgba(184, 137, 58, 0.06)", tilt: 0,    shadowBoost: 0,    lineHeight: 1.22 },
    "heart:soft":        { tintRGBA: "rgba(184, 137, 58, 0.06)", tilt: 0,    shadowBoost: 0,    lineHeight: 1.22 },
    "heart:hopeful":     { tintRGBA: "rgba(154, 123, 61, 0.05)", tilt: 0,    shadowBoost: 0,    lineHeight: 1.18 },

    // ─── Healing ───────────────────────────────────────────────
    "healing:peaceful":  { tintRGBA: "rgba(184, 137, 58, 0.06)", tilt: 0,    shadowBoost: 0,    lineHeight: 1.22 },
    "healing:tender":    { tintRGBA: "rgba(184, 137, 58, 0.06)", tilt: 0,    shadowBoost: 0,    lineHeight: 1.22 },
    "healing:grounded":  { tintRGBA: "rgba(184, 137, 58, 0.05)", tilt: 0,    shadowBoost: 0.05, lineHeight: 1.18 },

    // ─── Faith ─────────────────────────────────────────────────
    "faith:reverent":    { tintRGBA: "rgba(154, 123, 61, 0.08)", tilt: 0,    shadowBoost: 0.08, lineHeight: 1.20 },
    "faith:surrender":   { tintRGBA: "rgba(154, 123, 61, 0.06)", tilt: 0,    shadowBoost: 0,    lineHeight: 1.22 },
    "faith:grateful":    { tintRGBA: "rgba(154, 123, 61, 0.06)", tilt: 0,    shadowBoost: 0,    lineHeight: 1.20 },

    // ─── Drive ─────────────────────────────────────────────────
    "drive:relentless":  { tintRGBA: "rgba(43, 43, 43, 0.10)",   tilt: 0,    shadowBoost: 0.15, letterSpacing: "-0.01em", lineHeight: 1.12 },
    "drive:bold":        { tintRGBA: "rgba(43, 43, 43, 0.08)",   tilt: 0,    shadowBoost: 0.10, letterSpacing: "-0.005em", lineHeight: 1.14 },
    "drive:focused":     { tintRGBA: "rgba(43, 43, 43, 0.06)",   tilt: 0,    shadowBoost: 0.08, lineHeight: 1.14 },

    // ─── Funny ─────────────────────────────────────────────────
    "funny:unhinged":    { tintRGBA: "rgba(201, 122, 74, 0.08)", tilt: 1.4,  shadowBoost: 0,    lineHeight: 1.12 },
    "funny:chaotic":     { tintRGBA: "rgba(201, 122, 74, 0.08)", tilt: 1.0,  shadowBoost: 0,    lineHeight: 1.14 },
    "funny:petty":       { tintRGBA: "rgba(201, 122, 74, 0.06)", tilt: 0.6,  shadowBoost: 0,    lineHeight: 1.16 },
    "funny:deadpan":     { tintRGBA: "rgba(201, 122, 74, 0.05)", tilt: 0,    shadowBoost: 0,    letterSpacing: "0.01em",  lineHeight: 1.18 },

    // ─── Life ──────────────────────────────────────────────────
    "life:celebratory":  { tintRGBA: "rgba(160, 136, 79, 0.07)", tilt: 0,    shadowBoost: 0.05, lineHeight: 1.16 },
    "life:wistful":      { tintRGBA: "rgba(160, 136, 79, 0.06)", tilt: 0,    shadowBoost: 0,    lineHeight: 1.22 },
    "life:warm":         { tintRGBA: "rgba(160, 136, 79, 0.06)", tilt: 0,    shadowBoost: 0,    lineHeight: 1.18 },

    // ─── Civic ─────────────────────────────────────────────────
    "civic:fierce":      { tintRGBA: "rgba(124, 27, 38, 0.12)",  tilt: 0,    shadowBoost: 0.18, letterSpacing: "-0.015em", lineHeight: 1.10 },
    "civic:urgent":      { tintRGBA: "rgba(124, 27, 38, 0.10)",  tilt: 0,    shadowBoost: 0.14, letterSpacing: "-0.01em",  lineHeight: 1.12 },
    "civic:weary":       { tintRGBA: "rgba(124, 27, 38, 0.06)",  tilt: 0,    shadowBoost: 0.05, lineHeight: 1.20 },
    "civic:principled":  { tintRGBA: "rgba(124, 27, 38, 0.07)",  tilt: 0,    shadowBoost: 0.08, lineHeight: 1.14 },

    // ─── Banter ────────────────────────────────────────────────
    "banter:unhinged":   { tintRGBA: "rgba(184, 112, 76, 0.08)", tilt: 1.2,  shadowBoost: 0,    lineHeight: 1.12 },
    "banter:chaotic":    { tintRGBA: "rgba(184, 112, 76, 0.08)", tilt: 0.8,  shadowBoost: 0,    lineHeight: 1.14 },
    "banter:dramatic":   { tintRGBA: "rgba(184, 112, 76, 0.07)", tilt: 0.4,  shadowBoost: 0.05, lineHeight: 1.16 },
    "banter:deadpan":    { tintRGBA: "rgba(184, 112, 76, 0.05)", tilt: 0,    shadowBoost: 0,    letterSpacing: "0.01em",  lineHeight: 1.18 },
};

export const getClusterStyle = (laneKey, temperature) => {
    if (!laneKey || !temperature) return null;
    return CLUSTER_STYLES[`${laneKey}:${temperature}`] || null;
};
